import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { BeforeCapture, DropResult } from "@hello-pangea/dnd";
import { API } from "../../../api";
import type { Filter } from "../../../api/common";
import { Job, Deal } from "../../../models";
import { UserContext } from "../../../hooks/UserContext";
import { useCompanyContext } from "../../../hooks/CompanyContext";

// region Constants

const UNASSIGNED_DROPPABLE_ID = "unassigned";

type DealProperty = { name: string; label: string; getValue: (d: Deal) => string };

const STATIC_DEAL_PROPERTIES: DealProperty[] = [
	{ name: "season", label: "Season", getValue: (d) => d.season },
	{ name: "type", label: "Type", getValue: (d) => d.type },
	{ name: "labAccountNumber", label: "Lab Acct", getValue: (d) => d.labAccountNumber || "" },
	{ name: "syncMethod", label: "Sync", getValue: (d) => d.syncMethod || "" },
	{ name: "isPtsRequired", label: "Pts Required", getValue: (d) => (d.isPtsRequired ? "Yes" : "") },
	{ name: "customizer", label: "Customizer", getValue: (d) => d.customizer || "" },
	{ name: "density", label: "Density", getValue: (d) => `${d.density} acre` },
	{ name: "depth", label: "Depth", getValue: (d) => `${d.depth} in` },
	{ name: "testPackage", label: "Package", getValue: (d) => d.testPackage || "None" },
	{ name: "frequencyOfMicros", label: "Micros", getValue: (d) => `${d.frequencyOfMicros ?? 0}` },
	{ name: "labName", label: "Lab", getValue: (d) => d.labName || "" },
	{ name: "specialTag", label: "Tag", getValue: (d) => d.specialTag || "" },
	{
		name: "composite",
		label: "Composite",
		getValue: (d) => (d.isMultiComposite ? "Multi-Composite" : d.isComposite ? "Composite" : ""),
	},
];

function buildDealProperties(companyNameMap: Map<string, string>): DealProperty[] {
	return [
		{ name: "company", label: "Company", getValue: (d) => companyNameMap.get(d.companyId) || "" },
		...STATIC_DEAL_PROPERTIES,
	];
}

// Exported for the filter popover in the view
const DEAL_PROPERTIES = STATIC_DEAL_PROPERTIES;

export type DealSortOption = "default" | "density-asc" | "density-desc" | "depth-asc" | "depth-desc";
export type JobSortOption = "default" | "grower" | "farm" | "field";

// endregion

// region Helpers

function makeJobDraggableId(jobId: string) {
	return `job:${jobId}`;
}

function parseJobDraggableId(draggableId: string): string {
	return draggableId.replace(/^job:/, "");
}

// endregion

/**
 * Returns true if the deal's season matches the current or previous season.
 * Season format: "{YY}{variant}-{Suffix}" e.g. "26A-Spr", "25C-Fa"
 * Seasons by month: Winter=Dec-Jan, Spring=Feb-May, Summer=Jun-Jul, Fall=Aug-Nov
 */
function isRelevantSeason(dealSeason: string): boolean {
	const now = new Date();
	const month = now.getMonth(); // 0=Jan, 11=Dec
	const year2 = now.getFullYear() % 100;

	const seasonOrder = ["Win", "Spr", "Sum", "Fa"];
	let currentIdx: number;
	if (month === 11 || month === 0)
		currentIdx = 0; // Winter
	else if (month <= 4)
		currentIdx = 1; // Spring
	else if (month <= 6)
		currentIdx = 2; // Summer
	else currentIdx = 3; // Fall

	const prevIdx = (currentIdx - 1 + 4) % 4;
	const currentSuffix = seasonOrder[currentIdx];
	const prevSuffix = seasonOrder[prevIdx];

	// Parse: suffix is after last dash, year is first 2 digits
	const dashIdx = dealSeason.lastIndexOf("-");
	if (dashIdx === -1) return false;
	const suffix = dealSeason.substring(dashIdx + 1);
	const dealYear = parseInt(dealSeason.substring(0, 2), 10);
	if (isNaN(dealYear)) return false;

	if (suffix === currentSuffix && dealYear === year2) return true;
	if (suffix === prevSuffix && (dealYear === year2 || dealYear === year2 - 1)) return true;
	return false;
}

export { DEAL_PROPERTIES, UNASSIGNED_DROPPABLE_ID, makeJobDraggableId, parseJobDraggableId };

export const useUnmatchedDealsViewModel = () => {
	const currentUser = useContext(UserContext);
	const isDeveloper = currentUser?.isDeveloper ?? false;
	const { selectedCompany, companies } = useCompanyContext();

	const companyNameMap = useMemo(() => {
		const map = new Map<string, string>();
		for (const c of companies) {
			map.set(c.id, c.name);
		}
		return map;
	}, [companies]);

	const dealProperties = useMemo(() => buildDealProperties(companyNameMap), [companyNameMap]);

	// region Data loading

	const [jobs, setJobs] = useState<Job[]>([]);
	const [deals, setDeals] = useState<Deal[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (isDeveloper) {
			fetchData();
		}
	}, [isDeveloper, selectedCompany]);

	const [dealScope, setDealScopeState] = useState<"company" | "season" | "all">("season");
	const [includeInactive, setIncludeInactiveState] = useState(false);

	const fetchData = async (scope = dealScope, inactive = includeInactive) => {
		setLoading(true);
		setError(null);
		try {
			const jobsResult = await API.Data.Jobs.issues.getUnmatchedDealJobs();
			setJobs(jobsResult);

			if (scope === "all" || scope === "season") {
				const filter: Record<string, any> = { isConfigOnly: false };
				if (!inactive) filter.isActive = true;
				const allDeals = await API.Data.Deals.get.byFilter({
					fields: filter,
				} as Filter<Deal>);
				setDeals(scope === "season" ? allDeals.filter((d) => isRelevantSeason(d.season)) : allDeals);
			} else if (selectedCompany) {
				const companyDeals = await API.Data.Deals.get.byCompany(selectedCompany);
				const filtered = inactive ? companyDeals : companyDeals.filter((d) => d.isActive);
				setDeals(filtered.filter((d) => isRelevantSeason(d.season)));
			} else {
				setDeals([]);
			}
		} catch (err: any) {
			setError(`Failed to load data: ${err.message || "Unknown error"}`);
		} finally {
			setLoading(false);
		}
	};

	const setDealScope = (scope: "company" | "season" | "all") => {
		if (scope === dealScope) return;
		setDealScopeState(scope);
		fetchData(scope);
	};

	const setIncludeInactive = (value: boolean) => {
		setIncludeInactiveState(value);
		fetchData(dealScope, value);
	};

	// endregion

	// region Fix All (auto-match via Lambda)

	const [fixingIds, setFixingIds] = useState<Set<string>>(new Set());
	const [fixingAll, setFixingAll] = useState(false);
	const [countdown, setCountdown] = useState<number | null>(null);
	const countdownRef = useRef<ReturnType<typeof setInterval>>(undefined);

	useEffect(() => {
		return () => {
			if (countdownRef.current) {
				clearInterval(countdownRef.current);
			}
		};
	}, []);

	const sleepWithCountdown = useCallback((seconds: number) => {
		return new Promise<void>((resolve) => {
			if (countdownRef.current) {
				clearInterval(countdownRef.current);
			}
			setCountdown(seconds);
			countdownRef.current = setInterval(() => {
				setCountdown((prev) => {
					if (prev !== null && prev <= 1) {
						clearInterval(countdownRef.current);
						resolve();
						return null;
					}
					return prev !== null ? prev - 1 : null;
				});
			}, 1000);
		});
	}, []);

	const fixOne = async (id: string) => {
		setFixingIds((prev) => new Set(prev).add(id));
		setError(null);
		try {
			await API.Data.Jobs.issues.fixUnmatchedDealJobs([id]);
			await sleepWithCountdown(15);
			await fetchData();
		} catch (err: any) {
			setError(`Failed to fix job: ${err.message || "Unknown error"}`);
		} finally {
			setFixingIds((prev) => {
				const next = new Set(prev);
				next.delete(id);
				return next;
			});
		}
	};

	const fixAll = async () => {
		const selectedJobIds = [...selectedJobs].map(parseJobDraggableId);
		const ids = selectedJobIds.length > 0 ? selectedJobIds : jobs.map((j) => j.id);
		if (ids.length === 0) return;

		setFixingAll(true);
		setFixingIds(new Set(ids));
		setError(null);
		try {
			await API.Data.Jobs.issues.fixUnmatchedDealJobs(ids);
			await sleepWithCountdown(ids.length > 5 ? 180 : 15);
			await fetchData();
		} catch (err: any) {
			setError(`Failed to fix jobs: ${err.message || "Unknown error"}`);
		} finally {
			setFixingAll(false);
			setFixingIds(new Set());
			clearSelection();
		}
	};

	// endregion

	// region Staged assignments (drag-and-drop)

	// Map<jobId, dealId> — local staging before "Apply"
	const [pendingAssignments, setPendingAssignments] = useState<Map<string, string>>(new Map());
	const [applying, setApplying] = useState(false);

	const pendingCount = pendingAssignments.size;

	const stageAssignment = useCallback((jobIds: string[], dealId: string) => {
		setPendingAssignments((prev) => {
			const next = new Map(prev);
			for (const id of jobIds) {
				next.set(id, dealId);
			}
			return next;
		});
	}, []);

	const unstageAssignment = useCallback((jobIds: string[]) => {
		setPendingAssignments((prev) => {
			const next = new Map(prev);
			for (const id of jobIds) {
				next.delete(id);
			}
			return next;
		});
	}, []);

	const applyAssignments = async () => {
		if (pendingAssignments.size === 0) return;
		setApplying(true);
		setError(null);
		try {
			const entries = [...pendingAssignments.entries()];
			const jobsToUpdate = entries.map(([jobId, dealId]) => {
				const job = jobs.find((j) => j.id === jobId)!;
				return { ...job, dealIds: [dealId] };
			});
			await API.Data.Jobs.update.many(jobsToUpdate);
			setPendingAssignments(new Map());
			clearDealFilters();
			setDealSearchText("");
			setJobSearchText("");
			await fetchData();
		} catch (err: any) {
			setError(`Failed to apply assignments: ${err.message || "Unknown error"}`);
		} finally {
			setApplying(false);
		}
	};

	// Jobs staged to each deal
	const jobsByDeal = useMemo(() => {
		const map = new Map<string, Job[]>();
		for (const deal of deals) {
			map.set(deal.id, []);
		}
		for (const [jobId, dealId] of pendingAssignments) {
			const job = jobs.find((j) => j.id === jobId);
			if (job && map.has(dealId)) {
				map.get(dealId)!.push(job);
			}
		}
		return map;
	}, [deals, jobs, pendingAssignments]);

	// endregion

	// region Job search

	const [jobSearchText, setJobSearchText] = useState("");
	const [jobSortOption, setJobSortOption] = useState<JobSortOption>("default");

	const unassignedJobs = useMemo(() => {
		let result = jobs.filter((j) => !pendingAssignments.has(j.id));
		const trimmed = jobSearchText.trim().toLowerCase();
		if (trimmed) {
			result = result.filter((j) => {
				const haystack = [j.growerName, j.farmName, j.fieldName, j.companyName, j.status]
					.filter(Boolean)
					.join(" ")
					.toLowerCase();
				return trimmed.split(/\s+/).every((token) => haystack.includes(token));
			});
		}
		if (jobSortOption !== "default") {
			result = [...result].sort((a, b) => {
				const getter =
					jobSortOption === "grower"
						? (j: Job) => j.growerName || ""
						: jobSortOption === "farm"
							? (j: Job) => j.farmName || ""
							: (j: Job) => j.fieldName || "";
				return getter(a).localeCompare(getter(b));
			});
		}
		return result;
	}, [jobs, pendingAssignments, jobSearchText, jobSortOption]);

	// endregion

	// region Deal filter / sort / search

	const [dealSortOption, setDealSortOption] = useState<DealSortOption>("default");
	const [dealFilters, setDealFilters] = useState<Record<string, Set<string>>>({});
	const [dealSearchText, setDealSearchText] = useState("");
	const [hoveredChip, setHoveredChip] = useState<{ propertyName: string; value: string } | null>(null);

	const setDealFilter = useCallback((propertyName: string, values: Set<string>) => {
		setDealFilters((prev) => ({ ...prev, [propertyName]: values }));
	}, []);

	const clearDealFilters = useCallback(() => {
		setDealFilters({});
	}, []);

	const dealFilterOptions = useMemo((): Record<string, string[]> => {
		const result: Record<string, string[]> = {};
		for (const prop of dealProperties) {
			const values = deals.map(prop.getValue).filter(Boolean);
			result[prop.name] = [...new Set(values)].sort();
		}
		return result;
	}, [deals, dealProperties]);

	const filteredSortedDeals = useMemo(() => {
		const trimmed = dealSearchText.trim().toLowerCase();
		let filtered = deals.filter((deal) => {
			for (const prop of dealProperties) {
				const filterSet = dealFilters[prop.name];
				if (filterSet && filterSet.size > 0) {
					const value = prop.getValue(deal);
					if (!filterSet.has(value)) return false;
				}
			}
			if (trimmed) {
				const tokens = trimmed.split(/\s+/);
				const values = dealProperties.map((prop) => prop.getValue(deal).toLowerCase());
				if (!tokens.every((token) => values.some((v) => v.includes(token)))) return false;
			}
			return true;
		});

		if (dealSortOption !== "default") {
			filtered = [...filtered].sort((a, b) => {
				switch (dealSortOption) {
					case "density-asc":
						return a.density - b.density;
					case "density-desc":
						return b.density - a.density;
					case "depth-asc":
						return a.depth - b.depth;
					case "depth-desc":
						return b.depth - a.depth;
					default:
						return 0;
				}
			});
		}

		return filtered;
	}, [deals, dealFilters, dealSortOption, dealSearchText]);

	const activeDealFilterCount = useMemo(
		() => Object.values(dealFilters).filter((s) => s.size > 0).length,
		[dealFilters],
	);

	// endregion

	// region Drag-and-drop multi-select

	const [selectedJobs, setSelectedJobs] = useState<Set<string>>(new Set());
	const lastClickedItem = useRef<string | null>(null);
	const [draggingSelectedCount, setDraggingSelectedCount] = useState(0);

	const visibleItemOrder = useMemo(() => {
		const order: string[] = [];
		for (const job of unassignedJobs) {
			order.push(makeJobDraggableId(job.id));
		}
		for (const deal of filteredSortedDeals) {
			for (const job of jobsByDeal.get(deal.id) || []) {
				order.push(makeJobDraggableId(job.id));
			}
		}
		return order;
	}, [unassignedJobs, filteredSortedDeals, jobsByDeal]);

	const handleItemClick = useCallback(
		(draggableId: string, shiftKey: boolean) => {
			if (shiftKey && lastClickedItem.current) {
				const lastIdx = visibleItemOrder.indexOf(lastClickedItem.current);
				const curIdx = visibleItemOrder.indexOf(draggableId);
				if (lastIdx !== -1 && curIdx !== -1) {
					const start = Math.min(lastIdx, curIdx);
					const end = Math.max(lastIdx, curIdx);
					setSelectedJobs((prev) => {
						const next = new Set(prev);
						for (let i = start; i <= end; i++) {
							next.add(visibleItemOrder[i]);
						}
						return next;
					});
				} else {
					setSelectedJobs((prev) => new Set(prev).add(draggableId));
				}
				lastClickedItem.current = draggableId;
				return;
			}
			setSelectedJobs((prev) => {
				const next = new Set(prev);
				if (next.has(draggableId)) next.delete(draggableId);
				else next.add(draggableId);
				return next;
			});
			lastClickedItem.current = draggableId;
		},
		[visibleItemOrder],
	);

	const clearSelection = useCallback(() => {
		setSelectedJobs(new Set());
		lastClickedItem.current = null;
	}, []);

	const handleBeforeCapture = useCallback(
		(before: BeforeCapture) => {
			if (selectedJobs.has(before.draggableId)) {
				setDraggingSelectedCount(selectedJobs.size);
			} else {
				setDraggingSelectedCount(0);
			}
		},
		[selectedJobs],
	);

	const handleDragEnd = useCallback(
		(result: DropResult) => {
			const { draggableId, destination } = result;
			setDraggingSelectedCount(0);
			if (!destination) return;

			const targetId = destination.droppableId;
			const isMultiDrag = selectedJobs.has(draggableId) && selectedJobs.size > 1;

			const jobIds = isMultiDrag ? [...selectedJobs].map(parseJobDraggableId) : [parseJobDraggableId(draggableId)];

			if (targetId === UNASSIGNED_DROPPABLE_ID) {
				unstageAssignment(jobIds);
			} else {
				stageAssignment(jobIds, targetId);
			}

			setSelectedJobs(new Set());
			lastClickedItem.current = null;
		},
		[selectedJobs, stageAssignment, unstageAssignment],
	);

	const loadDealsByIds = async (ids: string[]) => {
		try {
			const fetched = await API.Data.Deals.get.byIds(ids);
			setDeals(fetched);
			setDealFilters({});
			setDealSearchText("");
		} catch (err: any) {
			setError(`Failed to load deals: ${err.message || "Unknown error"}`);
		}
	};

	// endregion

	return {
		isDeveloper,
		// Data
		companyNameMap,
		dealProperties,
		jobs,
		deals,
		loading,
		error,
		dealScope,
		setDealScope,
		includeInactive,
		setIncludeInactive,
		loadDealsByIds,
		// Fix All
		fixingIds,
		fixingAll,
		fixOne,
		fixAll,
		countdown,
		// Staged assignments
		pendingAssignments,
		pendingCount,
		applying,
		applyAssignments,
		jobsByDeal,
		// Job search/sort
		jobSearchText,
		setJobSearchText,
		jobSortOption,
		setJobSortOption,
		unassignedJobs,
		// Deal filter/sort/search
		dealSortOption,
		setDealSortOption,
		dealFilters,
		setDealFilter,
		clearDealFilters,
		dealFilterOptions,
		filteredSortedDeals,
		activeDealFilterCount,
		dealSearchText,
		setDealSearchText,
		hoveredChip,
		setHoveredChip,
		// DnD
		selectedJobs,
		draggingSelectedCount,
		handleItemClick,
		clearSelection,
		handleBeforeCapture,
		handleDragEnd,
		UNASSIGNED_DROPPABLE_ID,
	};
};
